import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { api } from "../../api/axios";
import { toast } from "react-hot-toast";
import { Table, Badge, Modal, Button as BsButton } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { AllClasses } from "../../configs/allClasses";
import { getCurrentTerm } from "../../services/billService";
import { PATH_DASHBOARD } from "../../routes/paths";
import { useCurrentTermAndSession } from "../../hooks/useCurrentTermAndSession";

const getClassName = (code) => {
  const found = AllClasses.find((c) => c.code === code);
  return found ? found.name : code || "—";
};

const deriveSession = (date) => {
  const d = date ? new Date(date) : new Date();
  const y = d.getFullYear();
  return d.getMonth() + 1 >= 8 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
};

const STATUS_COLORS = {
  draft: "secondary",
  issued: "primary",
  partial: "warning",
  paid: "success",
};

export function FamilyBills() {
  const navigate = useNavigate();

  const { currentSession, currentTerm, isLoading: hookLoading } = useCurrentTermAndSession();
  const [isInitialized, setIsInitialized] = useState(false);

  const [session, setSession] = useState("");
  const [term, setTerm] = useState("");

  useEffect(() => {
    if (currentSession && currentTerm && !isInitialized) {
      setSession(currentSession);
      setTerm(currentTerm);
      setIsInitialized(true);
    } else if (!hookLoading && !isInitialized) {
      setIsInitialized(true);
    }
  }, [currentSession, currentTerm, hookLoading, isInitialized]);

  const [familyData, setFamilyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const appUrl = process.env.REACT_APP_URL || "http://localhost:4000/api/v1";
  const token = localStorage.getItem("token");

  // Rollover state
  const [showRollover, setShowRollover] = useState(false);
  const [rolloverData, setRolloverData] = useState({
    fromSession: "2024/2025",
    fromTerm: "SECOND TERM",
    toSession: "2024/2025",
    toTerm: "THIRD TERM",
  });
  const [rollingOver, setRollingOver] = useState(false);

  // Auto-load current term (Replaced by hook)

  const fetchFamilyData = React.useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch all family bills for the session/term
      const billsRes = await api.get(
        `/bills/family?session=${session}&term=${term}`,
      );
      const rawFamilyBills = billsRes.data.data || [];

      // 2. Fetch all students to map admission numbers to names/classes
      const studentsRes = await axios.get(
        `${appUrl}/users?role=student&limit=5000`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const studentList =
        studentsRes.data.data.list || studentsRes.data.data || [];
      const studentMap = studentList.reduce((acc, student) => {
        if (student.admissionNumber) {
          acc[student.admissionNumber] = student;
        }
        return acc;
      }, {});

      // 3. Map the data together perfectly
      const results = rawFamilyBills.map((fb) => {
        const parent = fb.parentId || {};

        // Populate wards with real student objects instead of just admission strings
        const populatedWards = Array.isArray(parent.wards)
          ? parent.wards.map((admNo) => {
            const student = studentMap[admNo] || {
              firstName: "Unknown",
              lastName: "",
              admissionNumber: admNo,
              currentClass: "—",
            };
            return { student };
          })
          : [];

        return {
          familyBill: fb,
          parent,
          wards: populatedWards,
        };
      });

      setFamilyData(results);
    } catch (err) {
      toast.error("Failed to load family bills");
      console.error("Family bills fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [session, term, appUrl, token]);

  useEffect(() => {
    if (isInitialized && session && term) fetchFamilyData();
  }, [fetchFamilyData, isInitialized, session, term]);

  const filteredFamilies = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return familyData;
    return familyData.filter(({ parent, wards, familyBill }) => {
      const parentMatch =
        parent.firstName?.toLowerCase().includes(q) ||
        parent.lastName?.toLowerCase().includes(q) ||
        parent.email?.toLowerCase().includes(q) ||
        parent.tel?.includes(q);
      const wardMatch = wards.some(
        (w) =>
          w.student.firstName?.toLowerCase().includes(q) ||
          w.student.lastName?.toLowerCase().includes(q) ||
          w.student.admissionNumber?.toLowerCase().includes(q),
      );
      return parentMatch || wardMatch;
    });
  }, [familyData, searchQuery]);

  const handleRollOver = async () => {
    if (!window.confirm("Are you sure you want to roll over all outstanding balances? This cannot be undone automatically.")) {
      return;
    }
    setRollingOver(true);
    const tid = toast.loading("Rolling over outstanding balances...");
    try {
      const res = await axios.post(
        `${appUrl}/fee-assignments/roll-over`,
        rolloverData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.data.message || "Rollover successful", { id: tid });
      setShowRollover(false);
      fetchFamilyData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Rollover failed", { id: tid });
    } finally {
      setRollingOver(false);
    }
  };

  return (
    <Container>
      <Header>
        <div>
          <h3>Family Bills</h3>
          <p>
            View each family's bill summary. Click the eye icon for the full
            breakdown.
          </p>
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <BsButton
            variant="outline-danger"
            size="sm"
            onClick={() => setShowRollover(true)}
          >
            <Icon icon="mdi:bank-transfer" width="18" style={{ marginRight: 6 }} />
            Roll Over Outstanding
          </BsButton>
          {currentTerm && (
            <CurrentTermBadge>
              <Icon icon="mdi:calendar-check" width="18" />
              <span>
                Active Term: <strong>{currentTerm}</strong> &nbsp;|&nbsp;{" "}
                Session: <strong>{currentSession}</strong>
              </span>
            </CurrentTermBadge>
          )}
        </div>
      </Header>

      <Controls>
        <SelectGroup>
          <label>Academic Session</label>
          <select value={session} onChange={(e) => setSession(e.target.value)}>
            <option value="2023/2024">2023/2024</option>
            <option value="2024/2025">2024/2025</option>
            <option value="2025/2026">2025/2026</option>
            <option value="2026/2027">2026/2027</option>
            <option value="2027/2028">2027/2028</option>
            <option value="2028/2029">2028/2029</option>

          </select>
        </SelectGroup>

        <SelectGroup>
          <label>Term</label>
          <select value={term} onChange={(e) => setTerm(e.target.value)}>
            <option value="FIRST TERM">FIRST TERM</option>
            <option value="SECOND TERM">SECOND TERM</option>
            <option value="THIRD TERM">THIRD TERM</option>
          </select>
        </SelectGroup>

        <SelectGroup style={{ flex: 1 }}>
          <label>Search</label>
          <SearchInput
            type="text"
            placeholder="Search parent name, ward name, admission no..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SelectGroup>

        <RefreshBtn onClick={fetchFamilyData} disabled={loading}>
          <Icon icon={loading ? "mdi:loading" : "mdi:refresh"} width="18" />
          {loading ? "Loading..." : "Refresh"}
        </RefreshBtn>
      </Controls>

      <Card>
        {loading ? (
          <EmptyState>
            <Icon icon="mdi:loading" width="40" color="#1a73e8" />
            <p>Loading family bills…</p>
          </EmptyState>
        ) : filteredFamilies.length === 0 ? (
          <EmptyState>
            <Icon icon="mdi:account-group-outline" width="48" color="#ccc" />
            <p>No parents found.</p>
          </EmptyState>
        ) : (
          <Table responsive hover>
            <thead>
              <tr>
                <th>Parent</th>
                <th>Phone</th>
                <th>Wards</th>
                <th>Total Bill (₦)</th>
                <th>Outstanding (₦)</th>
                <th>Status</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {filteredFamilies.map(({ parent, wards, familyBill }) => {
                const totalBill = familyBill.totalAmount || 0;
                const balance = familyBill.balance || 0;
                const status = familyBill.status;
                const hasAnyBill = totalBill > 0 || status;

                return (
                  <tr key={familyBill._id}>
                    <td>
                      <strong>
                        {parent.firstName} {parent.lastName}
                      </strong>
                      <br />
                      <small style={{ color: "#888" }}>
                        {parent.email || "—"}
                      </small>
                    </td>
                    <td>{parent.tel || "—"}</td>
                    <td>
                      {wards.length === 0 ? (
                        <NoBillTag>No wards</NoBillTag>
                      ) : (
                        wards.map((w, i) => (
                          <WardTag key={w.student._id || i}>
                            {w.student.firstName} {w.student.lastName}{" "}
                            <span style={{ color: "#888", fontSize: 11 }}>
                              ({getClassName(w.student.currentClass)})
                            </span>
                          </WardTag>
                        ))
                      )}
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {hasAnyBill ? `₦${totalBill.toLocaleString()}` : "—"}
                    </td>
                    <td
                      style={{
                        fontWeight: 700,
                        color: balance > 0 ? "#dc3545" : "#28a745",
                      }}
                    >
                      {hasAnyBill ? `₦${balance.toLocaleString()}` : "—"}
                    </td>
                    <td>
                      {status ? (
                        <Badge bg={STATUS_COLORS[status] || "secondary"}>
                          {status.toUpperCase()}
                        </Badge>
                      ) : (
                        <NoBillTag>Draft</NoBillTag>
                      )}
                    </td>
                    <td>
                      <ActionBtn
                        onClick={() =>
                          navigate(
                            `${PATH_DASHBOARD.admin.familyBills}/${parent._id}`,
                          )
                        }
                        title="View full family bill breakdown"
                      >
                        <Icon icon="mdi:eye" color="#1a73e8" width="22" />
                      </ActionBtn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Rollover Modal */}
      <Modal show={showRollover} onHide={() => !rollingOver && setShowRollover(false)} centered>
        <Modal.Header closeButton={!rollingOver}>
          <Modal.Title>Roll Over Outstanding</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 14, color: "#555" }}>
            This will collect all unpaid family balances from the source term and stamp them as "Opening Outstanding / Arrears" on the students' bills in the target term.
          </p>
          <div style={{ display: "flex", gap: 10, marginBottom: 15 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#444" }}>From Session</label>
              <select
                className="form-control mt-1"
                value={rolloverData.fromSession}
                onChange={(e) => setRolloverData({ ...rolloverData, fromSession: e.target.value })}
              >
                <option value="2023/2024">2023/2024</option>
                <option value="2024/2025">2024/2025</option>
                <option value="2025/2026">2025/2026</option>
                <option value="2026/2027">2026/2027</option>
                <option value="2027/2028">2027/2028</option>
                <option value="2028/2029">2028/2029</option>

              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#444" }}>From Term</label>
              <select
                className="form-control mt-1"
                value={rolloverData.fromTerm}
                onChange={(e) => setRolloverData({ ...rolloverData, fromTerm: e.target.value })}
              >
                <option value="FIRST TERM">FIRST TERM</option>
                <option value="SECOND TERM">SECOND TERM</option>
                <option value="THIRD TERM">THIRD TERM</option>
              </select>
            </div>
          </div>
          <div style={{ textAlign: "center", color: "#888", marginBottom: 15 }}>
            <Icon icon="mdi:arrow-down" width="24" />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#444" }}>To Session</label>
              <select
                className="form-control mt-1"
                value={rolloverData.toSession}
                onChange={(e) => setRolloverData({ ...rolloverData, toSession: e.target.value })}
              >
                <option value="2023/2024">2023/2024</option>
                <option value="2024/2025">2024/2025</option>
                <option value="2025/2026">2025/2026</option>
                <option value="2026/2027">2026/2027</option>
                <option value="2027/2028">2027/2028</option>
                <option value="2028/2029">2028/2029</option>

              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 13, fontWeight: 500, color: "#444" }}>To Term</label>
              <select
                className="form-control mt-1"
                value={rolloverData.toTerm}
                onChange={(e) => setRolloverData({ ...rolloverData, toTerm: e.target.value })}
              >
                <option value="FIRST TERM">FIRST TERM</option>
                <option value="SECOND TERM">SECOND TERM</option>
                <option value="THIRD TERM">THIRD TERM</option>
              </select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <BsButton variant="light" onClick={() => setShowRollover(false)} disabled={rollingOver}>Cancel</BsButton>
          <BsButton variant="danger" onClick={handleRollOver} disabled={rollingOver}>
            {rollingOver ? "Rolling Over..." : "Execute Rollover"}
          </BsButton>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}

// ─── Styled Components ─────────────────────────────────────────────────────────

const Container = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 25px;
  h3 {
    margin: 0;
    color: #333;
  }
  p {
    color: #666;
    margin-top: 5px;
  }
`;

const CurrentTermBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #e8f0fe;
  color: #1a73e8;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  align-self: center;
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
  margin-bottom: 20px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const SelectGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  label {
    font-size: 14px;
    font-weight: 500;
    color: #444;
  }
  select {
    padding: 10px 15px;
    border-radius: 6px;
    border: 1px solid #ddd;
    min-width: 180px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  outline: none;
  font-size: 14px;
`;

const RefreshBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: #1a73e8;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-end;
  &:hover {
    opacity: 0.85;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Card = styled.div`
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const EmptyState = styled.div`
  padding: 50px 40px;
  text-align: center;
  color: #777;
  p {
    margin: 10px 0 4px;
    font-size: 16px;
  }
`;

const ActionBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  &:hover {
    opacity: 0.7;
  }
`;

const NoBillTag = styled.span`
  font-size: 12px;
  color: #aaa;
  font-style: italic;
`;

const WardTag = styled.div`
  font-size: 13px;
  color: #333;
  padding: 2px 0;
`;
